import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UserSubscription {
  plan: 'basic' | 'standard';
  status: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  roles: string[];
  subscription: UserSubscription | null;
  isPtaAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasPlan: (plan: 'basic' | 'standard') => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async (userId: string): Promise<{
      roles: string[];
      subscription: UserSubscription | null;
      isPtaAdmin: boolean;
    }> => {
      try {
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);

        if (rolesError) throw rolesError;
        const fetchedRoles = rolesData?.map(r => r.role) || [];

        const { data: memberData, error: memberError } = await supabase
          .from('members')
          .select('pta_id, is_pta_admin')
          .eq('user_id', userId)
          .maybeSingle();

        if (memberError) throw memberError;
        const fetchedIsPtaAdmin = memberData?.is_pta_admin || false;

        let fetchedSubscription: UserSubscription | null = null;
        if (memberData?.pta_id) {
          let { data: subData, error: subError } = await supabase
            .from('subscriptions')
            .select('plan, status, expires_at')
            .eq('pta_id', memberData.pta_id)
            .in('status', ['active', 'trial'])
            .maybeSingle();

          if (subError) throw subError;

          if (!subData) {
            const { data: expiredSub } = await supabase
              .from('subscriptions')
              .select('plan, status, expires_at')
              .eq('pta_id', memberData.pta_id)
              .eq('status', 'trial_expired')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (expiredSub) {
              subData = expiredSub;
            }
          }

          if (subData?.expires_at && new Date(subData.expires_at) < new Date()) {
            await supabase
              .from('subscriptions')
              .update({ status: 'expired' })
              .eq('pta_id', memberData.pta_id)
              .eq('status', 'active');
            
            fetchedSubscription = null;
          } else {
            fetchedSubscription = subData;
          }
        }

        return {
          roles: fetchedRoles,
          subscription: fetchedSubscription,
          isPtaAdmin: fetchedIsPtaAdmin
        };
      } catch (error: any) {
        console.error('Error fetching user data:', error);
        toast({
          title: "Error",
          description: "Failed to load user data",
          variant: "destructive",
        });
        return { roles: [], subscription: null, isPtaAdmin: false };
      }
    };

    const applyUserData = (data: { roles: string[]; subscription: UserSubscription | null; isPtaAdmin: boolean }) => {
      if (!isMounted) return;
      setRoles(data.roles);
      setSubscription(data.subscription);
      setIsPtaAdmin(data.isPtaAdmin);
    };

    const clearUserData = () => {
      if (!isMounted) return;
      setRoles([]);
      setSubscription(null);
      setIsPtaAdmin(false);
    };

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[AuthProvider] onAuthStateChange:', event, !!newSession?.user);
        
        if (!isMounted) return;
        
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          fetchUserData(newSession.user.id).then(applyUserData);
        } else {
          clearUserData();
        }
      }
    );

    const initializeAuth = async () => {
      console.log('[AuthProvider] Initializing auth...');
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        console.log('[AuthProvider] Initial session:', !!initialSession?.user);
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          const userData = await fetchUserData(initialSession.user.id);
          if (isMounted) {
            applyUserData(userData);
          }
        }
      } catch (error) {
        console.error('[AuthProvider] Error during initialization:', error);
      } finally {
        if (isMounted) {
          console.log('[AuthProvider] Initialization complete, setting loading=false');
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      authSubscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/pta-admin`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { full_name: fullName },
      }
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    return { data, error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) console.warn("Sign out warning:", error.message);
    } catch (e) {
      console.warn("Sign out exception:", e);
    }
    
    setUser(null);
    setSession(null);
    setRoles([]);
    setSubscription(null);
    setIsPtaAdmin(false);
    
    window.location.href = '/auth';
  };

  const hasRole = (role: string) => roles.includes(role);
  const hasPlan = (plan: 'basic' | 'standard') => subscription?.plan === plan || (plan === 'basic' && subscription?.plan === 'standard');

  return (
    <AuthContext.Provider value={{
      user, session, roles, subscription, isPtaAdmin, loading,
      signIn, signUp, signOut, hasRole, hasPlan,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
